import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { getDateFormatString } from '@shared/core/i18n/utils';
import { getDateFormatterForLanguage } from '../formatters/dateFormatter';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGuestDisplayLocale } from './GuestDisplayLocaleContext';

export type LocaleContextType = Readonly<{
  language: string;
  dateFormat: string;
  dateInputMask: string;
  formatDate: ReturnType<typeof getDateFormatterForLanguage>;
}>;

export const LocaleContext = React.createContext<LocaleContextType | undefined>(undefined);

export const LocaleProvider: React.FC = ({ children }) => {
  const { i18n } = useTranslation();

  const language = i18n.language;

  const value = React.useMemo<LocaleContextType>(() => {
    return {
      language,
      ...getDateFormatString(language),
      formatDate: getDateFormatterForLanguage(language),
    };
  }, [language]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocaleContext = () => {
  const context = React.useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocaleContext() can only be used within a LocaleProvider.');
  }
  return context;
};

/**
 * Returns the locale/language guest-facing dates should format in.
 *
 * On guest surfaces wrapped in {@link GuestDisplayLocaleProvider}, this follows
 * the guest's selected UI language (PRNT-4590); everywhere else — admin previews,
 * reminders, planning — it stays the event's configured locale.
 * @returns Object with language property containing the BCP 47 language tag (e.g., 'en-US', 'es-ES', 'fr-FR')
 */
export const useEventLocale = (): { language: string } => {
  const guestDisplayLocale = useGuestDisplayLocale();
  const { eventInfo } = useEventInfo();
  const locale = eventInfo?.locale;
  return { language: guestDisplayLocale ?? locale ?? 'en-US' };
};
